import * as THREE from "three";
import type { EnemyDef } from "../config/GameConfig";
import type { CreatureRig } from "./CreatureBuilder";
import { applySkin, type SkinKind } from "./CreatureSkins";

// Geometrias de baixo custo reutilizadas entre criaturas, sem texturas externas.
const shapes = new Map<string, THREE.BufferGeometry>();
function shape(key: string, make: () => THREE.BufferGeometry): THREE.BufferGeometry {
  let g = shapes.get(key);
  if (!g) {
    g = make();
    g.userData.shared = true;
    shapes.set(key, g);
  }
  return g;
}
function oval(x: number, y: number, z: number): THREE.BufferGeometry {
  return shape(`oval:${x}:${y}:${z}`, () => new THREE.SphereGeometry(1, 14, 10).scale(x, y, z));
}
function cone(r: number, h: number): THREE.BufferGeometry {
  return shape(`horn:${r}:${h}`, () => new THREE.ConeGeometry(r, h, 7));
}
function gem(s: number): THREE.BufferGeometry {
  return shape(`gem:${s}`, () => new THREE.OctahedronGeometry(s, 0));
}
function mesh(parent: THREE.Object3D, geometry: THREE.BufferGeometry, material: THREE.Material, x: number, y: number, z: number): THREE.Mesh {
  const m = new THREE.Mesh(geometry, material);
  m.position.set(x, y, z);
  m.castShadow = true;
  parent.add(m);
  return m;
}
// 10.5K: materiais com PELE procedural (map + relevo) — leitura 3D muito melhor que cor chapada.
// `density` maior nas criaturas grandes (o padrão não vira "casco de tartaruga").
function material(color: number, skin: SkinKind, density = 2, emissive = 0x000000): THREE.MeshStandardMaterial {
  const m = new THREE.MeshStandardMaterial({ color, roughness: 0.72, metalness: 0.12, emissive, emissiveIntensity: 0.3 });
  return applySkin(m, skin, skin === "stone" ? 0.05 : 0.035, density);
}
function eyes(parent: THREE.Object3D, glow: THREE.Material, width: number, y: number, z: number, size = 1): void {
  for (const side of [-1, 1]) mesh(parent, oval(0.055 * size, 0.04 * size, 0.035 * size), glow, width * side, y, z).userData.eye = true; // 10.9BT
}
function leg(rig: CreatureRig, mat: THREE.Material, x: number, y: number, z: number, length: number, thick: number, claw?: THREE.Material): void {
  const pivot = new THREE.Group();
  pivot.position.set(x, y, z);
  mesh(pivot, oval(thick, length / 2, thick), mat, 0, -length / 2, 0);
  // articulação (joelho) + pata
  mesh(pivot, oval(thick * 1.15, thick * 1.15, thick * 1.15), mat, 0, -length * 0.5, thick * 0.15);
  mesh(pivot, oval(thick * 1.2, thick * 0.6, thick * 1.8), mat, 0, -length + thick * 0.4, thick * 0.4);
  if (claw) {
    for (const s of [-0.6, 0, 0.6]) {
      const c = mesh(pivot, cone(thick * 0.35, thick * 1.3), claw, s * thick, -length + thick * 0.35, thick * 1.9);
      c.rotation.x = Math.PI / 2;
    }
  }
  rig.group.add(pivot);
  rig.legs.push(pivot);
}

/** Silhuetas da floresta: inseto, predador, besta de pedra e guardião bípede. */
export function buildForestCreature(def: EnemyDef, rig: CreatureRig): boolean {
  if (!["voidling", "voidRunner", "voidBrute", "corruptedBrute"].includes(def.id)) return false;
  const elite = def.id === "corruptedBrute";
  const runner = def.id === "voidRunner";
  const brute = def.id === "voidBrute";
  const skinKind: SkinKind = elite ? "hide" : brute ? "stone" : runner ? "scales" : "chitin";
  const plateKind: SkinKind = brute ? "stone" : "chitin";
  const density = elite || brute ? 5 : 3;
  const skin = material(elite ? 0x3a4f5a : brute ? 0x5e6f66 : runner ? 0x36545e : 0x4c6e60, skinKind, density);
  const plates = material(elite ? 0x6d8f96 : brute ? 0x9aab9c : 0x80a397, plateKind, density);
  const horn = material(0xe8dfb8, "stone", 3);
  horn.roughness = 0.5;
  const glowColor = elite ? 0x91f4ff : 0xbdff8e;
  const glow = new THREE.MeshStandardMaterial({ color: glowColor, emissive: elite ? 0x42bfff : 0x71d94b, emissiveIntensity: 1.2, roughness: 0.3 }); // 10.9BT: 1.6 → 1.2 (bloom)
  rig.bodyMats.push(skin, plates, horn);
  rig.crystalMats.push(glow);

  const core = new THREE.Group();
  rig.group.add(core);
  rig.core = core;
  const head = new THREE.Group();
  rig.group.add(head);
  rig.head = head;

  if (elite) {
    // GUARDIÃO: pescoço e braços separados, pernas sob o tronco, coroa de chifres,
    // cristais nos ombros, garras e manto nas costas.
    mesh(core, oval(0.46, 0.58, 0.3), skin, 0, 1.12, 0);
    mesh(core, oval(0.36, 0.29, 0.22), plates, 0, 1.32, 0.21);
    mesh(core, oval(0.4, 0.2, 0.2), plates, 0, 0.82, 0.1); // cinturão de placas
    mesh(core, oval(0.5, 0.62, 0.08), plates, 0, 1.1, -0.28); // manto/escudo dorsal
    mesh(core, gem(0.14), glow, 0, 1.35, 0.43);
    for (const side of [-1, 1]) {
      leg(rig, skin, side * 0.23, 0.67, 0, 0.61, 0.16, horn);
      mesh(core, oval(0.27, 0.22, 0.29), plates, side * 0.49, 1.42, 0);
      mesh(core, gem(0.11), glow, side * 0.52, 1.6, 0.02); // cristal no ombro
      mesh(core, oval(0.15, 0.39, 0.16), skin, side * 0.56, 1.04, 0.06);
      mesh(core, oval(0.19, 0.19, 0.2), plates, side * 0.56, 0.72, 0.12);
      for (const k of [-1, 0, 1]) {
        const claw = mesh(core, cone(0.045, 0.24), horn, side * 0.56 + k * 0.07, 0.5, 0.2);
        claw.rotation.x = 0.35;
      }
      const spike = mesh(core, cone(0.11, 0.42), horn, side * 0.49, 1.76, -0.06);
      spike.rotation.z = side * -0.35;
    }
    head.position.set(0, 1.79, 0.03);
    mesh(head, oval(0.24, 0.27, 0.23), skin, 0, 0, 0);
    mesh(head, oval(0.16, 0.08, 0.12), plates, 0, -0.14, 0.18); // mandíbula
    eyes(head, glow, 0.1, 0.04, 0.225, 1.2);
    for (const side of [-1, 1]) {
      const h = mesh(head, cone(0.07, 0.4), horn, side * 0.17, 0.33, -0.03);
      h.rotation.z = side * -0.35;
      const h2 = mesh(head, cone(0.045, 0.22), horn, side * 0.09, 0.3, -0.12);
      h2.rotation.z = side * -0.15;
    }
    rig.height = 2.35;
  } else if (brute) {
    // STONEBACK: carapaça arqueada com espinhos de pedra e cristais, focinho, presas, 4 patas.
    mesh(core, oval(0.64, 0.49, 0.8), skin, 0, 0.78, -0.08);
    for (const z of [-0.45, -0.06, 0.33]) mesh(core, oval(0.64, 0.24, 0.26), plates, 0, 1.09, z);
    for (const [x, z, s] of [[0, -0.25, 1.2], [-0.3, 0.05, 0.9], [0.3, 0.05, 0.9]] as const) {
      const c = mesh(core, gem(0.16 * s), glow, x, 1.3, z);
      c.scale.y = 1.6;
    }
    for (const [x, z] of [[-0.42, -0.5], [0.42, -0.5], [-0.5, 0.2], [0.5, 0.2]] as const) {
      const sp = mesh(core, cone(0.09, 0.32), horn, x, 1.22, z);
      sp.rotation.z = x < 0 ? 0.35 : -0.35;
    }
    head.position.set(0, 0.64, 0.83);
    mesh(head, oval(0.38, 0.3, 0.39), skin, 0, 0, 0);
    mesh(head, oval(0.27, 0.18, 0.22), plates, 0, -0.09, 0.28);
    mesh(head, oval(0.22, 0.09, 0.2), skin, 0, -0.22, 0.24); // queixo
    eyes(head, glow, 0.23, 0.1, 0.28, 1.1);
    for (const side of [-1, 1]) {
      const h = mesh(head, cone(0.09, 0.5), horn, side * 0.29, 0.22, 0.28);
      h.rotation.x = -0.55;
      const tusk = mesh(head, cone(0.05, 0.28), horn, side * 0.16, -0.16, 0.38);
      tusk.rotation.x = -0.9;
      for (const z of [-0.48, 0.46]) leg(rig, skin, side * 0.48, 0.59, z, 0.5, 0.16, horn);
    }
    rig.height = 1.4;
  } else if (runner) {
    // RIFTCLAW: dorso alongado com crista de espinhos, focinho, orelhas, garras, cauda afilada.
    mesh(core, oval(0.28, 0.32, 0.63), skin, 0, 0.65, -0.05);
    mesh(core, oval(0.33, 0.37, 0.35), plates, 0, 0.72, 0.3);
    for (const z of [0.15, -0.05, -0.25, -0.45]) {
      const s = mesh(core, cone(0.05, 0.22), plates, 0, 0.98 - Math.abs(z + 0.15) * 0.25, z);
      s.rotation.x = -0.4;
    }
    mesh(core, gem(0.08), glow, 0, 0.9, 0.42); // cristal da nuca
    head.position.set(0, 0.92, 0.64);
    mesh(head, oval(0.25, 0.27, 0.28), skin, 0, 0, 0);
    mesh(head, oval(0.16, 0.13, 0.25), skin, 0, -0.09, 0.27);
    mesh(head, oval(0.13, 0.06, 0.2), plates, 0, -0.2, 0.25); // maxilar inferior
    mesh(head, gem(0.06), glow, 0, 0.16, 0.12); // cristal na testa
    eyes(head, glow, 0.16, 0.04, 0.23);
    for (const side of [-1, 1]) {
      const ear = mesh(head, cone(0.1, 0.31), plates, side * 0.17, 0.31, -0.06);
      ear.rotation.z = side * -0.2;
      const fang = mesh(head, cone(0.03, 0.14), horn, side * 0.08, -0.2, 0.42);
      fang.rotation.x = Math.PI;
      leg(rig, skin, side * 0.22, 0.57, 0.38, 0.51, 0.075, horn);
      leg(rig, skin, side * 0.22, 0.57, -0.4, 0.51, 0.075);
    }
    const tail = new THREE.Group();
    tail.position.set(0, 0.72, -0.62);
    const t = mesh(tail, cone(0.13, 0.74), skin, 0, 0.04, -0.29);
    t.rotation.x = -Math.PI / 2;
    mesh(tail, gem(0.06), glow, 0, 0.04, -0.66);
    rig.group.add(tail);
    rig.tail = tail;
    rig.height = 1.45;
  } else {
    // SHARDLING: inseto de seis patas, placas sobrepostas, antenas, cristais e listras de brilho.
    mesh(core, oval(0.35, 0.25, 0.48), skin, 0, 0.39, -0.06);
    mesh(core, oval(0.34, 0.17, 0.43), plates, 0, 0.56, -0.07);
    for (const z of [-0.3, -0.12, 0.06]) mesh(core, oval(0.3, 0.06, 0.07), plates, 0, 0.66, z); // anéis do abdômen
    for (const z of [-0.36, -0.18]) mesh(core, oval(0.16, 0.025, 0.03), glow, 0, 0.61, z); // listras de brilho
    for (const [z, s] of [[0.1, 1], [-0.08, 1.3], [-0.28, 0.9]] as const) mesh(core, gem(0.07 * s), glow, 0, 0.73, z);
    head.position.set(0, 0.4, 0.45);
    mesh(head, oval(0.23, 0.19, 0.2), skin, 0, 0, 0);
    eyes(head, glow, 0.12, 0.04, 0.18, 1.3);
    for (const side of [-1, 1]) {
      for (const z of [-0.32, 0, 0.3]) leg(rig, skin, side * 0.32, 0.29, z, 0.24, 0.045);
      const tusk = mesh(head, cone(0.055, 0.24), horn, side * 0.14, -0.04, 0.23);
      tusk.rotation.x = Math.PI / 2;
      const ant = mesh(head, cone(0.02, 0.34), plates, side * 0.1, 0.22, 0.12);
      ant.rotation.x = -0.9;
      ant.rotation.z = side * -0.4;
    }
    rig.height = 0.82;
  }
  return true;
}
